import React, { useEffect, useRef, useState } from 'react';
import { AppState } from '../../../redux/reducers';
import { useSelector, connect } from 'react-redux';
import {
  Button,
  Checkbox,
  Combobox,
  Input,
  MessageBarBody,
  MessageBarTitle,
  Option,
  Select,
  Switch,
  Textarea,
  makeStyles,
  shorthands,
  tokens
} from '@fluentui/react-components';
import MedicalServiceSelectorComponent from '../../../commons/component/Inputs/MedicalServiceSelector/MedicalServiceSelectorComponent';
import { useMedicalServiceCategories } from './useMedicalServiceCategories';
import { MedicalServiceCategoriesDrawerComponent } from '../MedicalServiceCategories/MedicalServiceCategories.Drawer.Component';
import { Settings16Regular } from '@fluentui/react-icons';
import './MedicalServiceForm.Styles.css';
import { Add20Regular, Dismiss12Regular } from '@fluentui/react-icons';
import { ErrorMessageComponent } from '../../Error/ErrorMessageComponent';
import { AvailableMedicalService } from '../../../models/Entities/MedicalServices/AvailableMedicalService';
import { MedicalServiceSpecialty } from '../../../models/Entities/MedicalServices/MedicalServiceSpecialty';

import { NewAvailableMedicalService } from '../../../models/Entities/MedicalServices/NewAvailableMedicalService';

// design guide
import { GuideDrawer, GuideDrawerSection } from '../../../theme/components/GuideDrawer';
import { GuideField, GuideFieldGrid, GuideHelpText } from '../../../theme/components/GuideField';
import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';

// own functions
import * as ownFunctions from './MedicalServiceForm.Functions';

////i18Next
import { useTranslation } from 'react-i18next';

// services
import * as ProceduresService from '../../../services/Procedures.Services';

// models
import { Procedure } from '../../../models/Entities/Procedures/Procedure';
import SpecialtyMultiSelector, {
  SelectedSpecialtyType
} from '../../MedicalSpecialties/SpecialtySelector/SpecialtyMultiSelectorComponent';
import { SpecialtyViewModel } from '../../../models/Entities/Specialties/SpecialtyModels';

interface FormValidation {
  success: boolean;
  errors: string[];
}

/** Sugerencia del buscador de prácticas (antes un ITag del TagPicker de Fluent v8). */
interface ProcedureSuggestion {
  key: number | string;
  name: string;
}

const useStyles = makeStyles({
  pills: {
    display: 'flex',
    flexWrap: 'wrap',
    columnGap: tokens.spacingHorizontalS,
    rowGap: tokens.spacingVerticalS
  },
  pill: {
    display: 'inline-flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalXS,
    backgroundColor: 'var(--ichtys-row-selected-bg)',
    color: 'var(--ichtys-pill-info-fg)',
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    ...shorthands.border(tokens.strokeWidthThin, 'solid', 'var(--ichtys-pill-info-border)'),
    ...shorthands.borderRadius(tokens.borderRadiusCircular),
    ...shorthands.padding(tokens.spacingVerticalXXS, tokens.spacingHorizontalXS, tokens.spacingVerticalXXS, tokens.spacingHorizontalM)
  },
  pillRemove: {
    minWidth: 'auto',
    color: 'var(--ichtys-pill-info-fg)'
  },
  empty: {
    fontStyle: 'italic'
  },
  capitalize: {
    textTransform: 'capitalize'
  }
});

export const MedicalServicesFormComponent = (props: ownFunctions.Props) => {
  const styles = useStyles();

  const [submittedForm, setSubmittedForm] = useState<boolean>(false);
  const [FormValidationStatus, setFormValidationStatus] = useState<
    FormValidation | undefined
  >();

  const MedicalServicesState = useSelector(
    (state: AppState) => state.MedicalServices
  );

  // MedicalInsurance state
  const [id, setId] = useState<number>();
  const [serviceNameReference, setServiceNameReference] = useState<string>();
  const [available, setAvailable] = useState<boolean>(false);
  const [isLinkedToSnowmed, setIsLinkedToSnowmed] = useState<boolean>(true);
  const [snowmedCode, setSnowmedCode] = useState<number>();
  const [snowmedName, setSnowmedName] = useState<string>();
  const [comments, setComments] = useState<string>();
  const [isSpecialtyPanelOpen, setIsSpecialtyPanelOpen] = useState<boolean>(false);

  // clasificación: código interno, ámbitos, categoría y padre (jerarquía)
  const [internalCode, setInternalCode] = useState<string>('');
  const [isAssistanceScope, setIsAssistanceScope] = useState<boolean>(true);
  const [isProtocolScope, setIsProtocolScope] = useState<boolean>(false);
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [parentServiceId, setParentServiceId] = useState<number | null>(null);
  const [parentServiceName, setParentServiceName] = useState<string | undefined>(undefined);
  const [categoriesDrawerOpen, setCategoriesDrawerOpen] = useState<boolean>(false);

  // procedure search (SNOMED)
  const [procedureQuery, setProcedureQuery] = useState<string>('');
  const [procedureSuggestions, setProcedureSuggestions] = useState<ProcedureSuggestion[]>([]);
  const lastSearch = useRef<number>(0);

  //useTranslation()
  const [t] = useTranslation();

  // Categorías del catálogo (si el gateway todavía no las enruta, el campo queda de sólo lectura).
  const categories = useMedicalServiceCategories(props.access_token, props.isOpen);

  // effects
  useEffect(() => {
    if (props.MedicalService) {
      setId(props.MedicalService.id);
      setServiceNameReference(props.MedicalService.serviceNameReference);
      setAvailable(props.MedicalService.available);
      setIsLinkedToSnowmed(props.MedicalService.isLinkedToSnowmed);
      setSnowmedCode(props.MedicalService.snowmedCode);
      setComments(props.MedicalService.comments);
      setSnowmedName(props.MedicalService.snowmedName);
      setInternalCode(props.MedicalService.internalCode ?? '');
      setIsAssistanceScope(props.MedicalService.isAssistanceScope ?? true);
      setIsProtocolScope(props.MedicalService.isProtocolScope ?? false);
      setCategoryId(props.MedicalService.categoryId ?? props.MedicalService.category?.id ?? null);
      setParentServiceId(props.MedicalService.parentServiceId ?? props.MedicalService.parent?.id ?? null);
      setParentServiceName(props.MedicalService.parent?.serviceNameReference);
    } else {
      ResetForm();
    }
    // Sólo cuando cambia el servicio abierto. El reductor crea un objeto nuevo cuando llegan o cambian sus
    // especialidades, y rehidratar en ese momento pisaba lo que el usuario ya había cargado (p. ej. el SNOMED).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.MedicalService?.id]);

  useEffect(() => {
    if (props.MedicalServiceId) {
      props.GetMedicalServiceSpecialties(props.MedicalServiceId);
    }
  }, [props.MedicalServiceId]);

  useEffect(() => {
    if (
      submittedForm &&
      MedicalServicesState &&
      (MedicalServicesState.successAddingNewOne ||
        MedicalServicesState.successUpdatingOne)
    ) {
      ResetForm();
      props.dismissPanel();
    }
  }, [
    MedicalServicesState?.successAddingNewOne,
    MedicalServicesState?.successUpdatingOne
  ]);

  // El buscador arranca vacío cada vez que se abre el panel (como el TagPicker, que se montaba de nuevo).
  useEffect(() => {
    if (!props.isOpen) {
      setProcedureQuery('');
      setProcedureSuggestions([]);
    }
  }, [props.isOpen]);

  const ResetForm = () => {
    setServiceNameReference(undefined);
    setAvailable(false);
    setId(undefined);
    setComments(undefined);
    setIsLinkedToSnowmed(true);
    setSnowmedCode(undefined);
    setSnowmedName(undefined);
    setInternalCode('');
    setIsAssistanceScope(true);
    setIsProtocolScope(false);
    setCategoryId(null);
    setParentServiceId(null);
    setParentServiceName(undefined);
    setFormValidationStatus(undefined);
    setSubmittedForm(false);
  };

  // Specialty handlers
  const selectedSpecialties: SelectedSpecialtyType[] = (
    props.MedicalService?.specialties ?? []
  ).map((s: MedicalServiceSpecialty) => ({
    specialtyId: s.specialtyId,
    ReferenceId: s.id
  }));

  const handleSpecialtySelect = (specialty: SpecialtyViewModel) => {
    if (id) {
      props.AddMedicalServiceSpecialty({
        medicalServiceId: id,
        specialtyId: specialty.id,
        specialtyName: specialty.name ?? undefined
      });
    }
  };

  const handleSpecialtyUnselect = (specialty: SelectedSpecialtyType) => {
    if (id) {
      props.RemoveMedicalServiceSpecialty(id, specialty.ReferenceId);
    }
  };
  const onChangeIdTextFieldValue = (items?: ProcedureSuggestion[] | undefined) => {
    if (items && items.length > 0) {
      setSnowmedCode(Number(items[0].key));
      setSnowmedName(items[0].name);
      if (serviceNameReference === '' || serviceNameReference === undefined)
        setServiceNameReference(items[0].name);
    }
  };

  const HandleSaveForm = () => {
    setSubmittedForm(true);
    if (ValidateForm()) {
      var CurrentValues: AvailableMedicalService | NewAvailableMedicalService;
      if (props.MedicalService) {
        CurrentValues = {
          id: props.MedicalService.id,
          serviceNameReference: serviceNameReference!,
          isLinkedToSnowmed: isLinkedToSnowmed,
          available: available,
          disableUntilDate: props.MedicalService.disableUntilDate,
          disableComment: props.MedicalService.disableComment,
          snowmedCode: snowmedCode,
          comments: comments,
          snowmedName: snowmedName,
          // La configuración se conserva tal como está (antes se pisaba con valores fijos al editar).
          enablePatientSelfManagement: props.MedicalService.enablePatientSelfManagement ?? false,
          serviceProvidedBy3rdParty: props.MedicalService.serviceProvidedBy3rdParty ?? false,
          enableforAppointment: props.MedicalService.enableforAppointment ?? true,
          onlyForAdministrativeUse: props.MedicalService.onlyForAdministrativeUse ?? false,
          internalCode: internalCode.trim() || null,
          isAssistanceScope,
          isProtocolScope,
          categoryId,
          parentServiceId
        };
        props.UpdateMedicalService(CurrentValues);
      } else {
        CurrentValues = {
          serviceNameReference: serviceNameReference!,
          isLinkedToSnowmed: isLinkedToSnowmed,
          available: available,
          disableUntilDate: undefined,
          disableComment: undefined,
          snowmedCode: snowmedCode,
          comments: comments,
          snowmedName: snowmedName,
          internalCode: internalCode.trim() || null,
          isAssistanceScope,
          isProtocolScope,
          categoryId,
          parentServiceId
        };
        props.AddMedicalService(CurrentValues);
      }
    }
  };

  const HandleCancelForm = () => {
    props.dismissPanel();
    ResetForm();
  };

  const filterSuggestedTags = async (
    filterText: string
  ): Promise<ProcedureSuggestion[]> => {

    if (props.access_token) {
      return await ProceduresService.GetProcedures(filterText, props.access_token).then(
        (procedures: Procedure[]) => {
          return procedures.map((item: Procedure) => {
            return { key: item.id, name: item.name };
          });
        });
    } else {
      return [] as ProcedureSuggestion[];
    }
  };

  const onProcedureQueryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const text = event.target.value;
    setProcedureQuery(text);
    const searchId = ++lastSearch.current;
    if (!text.trim()) {
      setProcedureSuggestions([]);
      return;
    }
    filterSuggestedTags(text)
      .then((suggestions) => {
        // sólo vale la respuesta de la última búsqueda
        if (searchId === lastSearch.current) setProcedureSuggestions(suggestions);
      })
      .catch(() => {
        if (searchId === lastSearch.current) setProcedureSuggestions([]);
      });
  };

  const isSaving = !!(MedicalServicesState?.isAddingNewOne || MedicalServicesState?.isUpdatingOne);

  const formFields = (
    <>
      <GuideDrawerSection title="Servicio Médico">
        <Switch
          label={t('medicalServiceForm.props.requireLinking')}
          checked={isLinkedToSnowmed}
          onChange={(_, data) => setIsLinkedToSnowmed(!!data.checked)}
        />
        {isLinkedToSnowmed && (
          <>
            {props.MedicalService && props.MedicalService.isLinkedToSnowmed && (
              <GuideHelpText data-testid="medical-service-current-link">
                <strong>{t('medicalServiceForm.props.currentLink')}</strong>
                {': '}
                {props.MedicalService.snowmedCode} |{' '}
                <span className={styles.capitalize}>{props.MedicalService.snowmedName}</span>
              </GuideHelpText>
            )}
            <GuideField
              label={t('medicalServiceForm.props.snowmed')}
              hint={t('medicalServiceForm.props.enterText')}
            >
              <Combobox
                freeform
                aria-label="Seleccione un servicio"
                value={procedureQuery}
                onChange={onProcedureQueryChange}
                onOptionSelect={(_, data) => {
                  const selected = procedureSuggestions.find((item) => String(item.key) === data.optionValue);
                  if (selected) {
                    onChangeIdTextFieldValue([selected]);
                    setProcedureQuery(selected.name);
                  }
                }}
              >
                {procedureQuery.trim() && procedureSuggestions.length === 0 ? (
                  <Option value="" text="" disabled>
                    No se encontro servicio
                  </Option>
                ) : (
                  procedureSuggestions.map((item) => (
                    <Option key={String(item.key)} value={String(item.key)} text={item.name}>
                      {item.name}
                    </Option>
                  ))
                )}
              </Combobox>
            </GuideField>
          </>
        )}
      </GuideDrawerSection>
      <GuideField
        label={t('medicalServiceForm.props.referenceName')}
        required
        error={
          submittedForm === true && serviceNameReference === undefined
            ? t('medicalServiceForm.props.requiredField')
            : undefined
        }
      >
        <Textarea
          name="txt_serviceNameReference"
          resize="vertical"
          autoComplete="off"
          onChange={(_, data) => setServiceNameReference(data.value || '')}
          value={serviceNameReference || ''}
        />
      </GuideField>
      <GuideField label={t('medicalServiceForm.props.comments')}>
        <Textarea
          name="txt_comentarios"
          resize="vertical"
          autoComplete="off"
          onChange={(_, data) => setComments(data.value || '')}
          value={comments || ''}
        />
      </GuideField>
      {props.MedicalService && (
        <Switch
          label={t('medicalServiceForm.props.serviceEnable')}
          checked={available}
          onChange={(_, data) => setAvailable(!!data.checked)}
        />
      )}

      <GuideDrawerSection title={t('medicalServiceForm.sections.classification')} description={t('medicalServiceForm.sections.classificationHint')}>
        <GuideFieldGrid>
          <GuideField label={t('medicalServiceForm.props.internalCode')} hint={t('medicalServiceForm.props.internalCodeHint')} counter={{ value: internalCode.length, max: 50 }}>
            <Input
              value={internalCode}
              maxLength={50}
              autoComplete="off"
              onChange={(_, data) => setInternalCode(data.value)}
              data-testid="medical-service-internal-code"
            />
          </GuideField>
          <GuideField
            label={t('medicalServiceForm.props.category')}
            hint={categories.unavailable ? t('medicalServiceForm.props.categoriesUnavailable') : t('medicalServiceForm.props.categoryHint')}
          >
            <div className="msf-category">
              {categories.unavailable ? (
                <Input value={props.MedicalService?.category?.name ?? t('medicalServiceForm.props.categoryNone')} disabled readOnly />
              ) : (
                <Select
                  value={categoryId === null ? '' : String(categoryId)}
                  disabled={categories.loading}
                  onChange={(_, data) => setCategoryId(data.value ? Number(data.value) : null)}
                  data-testid="medical-service-category"
                >
                  <option value="">{categories.loading ? t('medicalServiceForm.props.categoriesLoading') : t('medicalServiceForm.props.categoryNone')}</option>
                  {categories.categories.map((category) => (
                    <option key={category.id} value={String(category.id)}>{category.name}</option>
                  ))}
                  {categoryId !== null && !categories.categories.some((category) => category.id === categoryId) && props.MedicalService?.category && (
                    <option value={String(categoryId)}>{props.MedicalService.category.name}</option>
                  )}
                </Select>
              )}
              {/* Alta y administración de categorías sin salir del servicio */}
              <Button
                appearance="secondary"
                icon={<Settings16Regular />}
                onClick={() => setCategoriesDrawerOpen(true)}
                title={t('medicalServiceForm.props.manageCategories')}
                aria-label={t('medicalServiceForm.props.manageCategories')}
                data-testid="medical-service-manage-categories"
              >
                {t('medicalServiceForm.props.manageCategoriesShort')}
              </Button>
            </div>
          </GuideField>
        </GuideFieldGrid>

        <GuideField label={t('medicalServiceForm.props.scope')} hint={t('medicalServiceForm.props.scopeHint')}>
          <div className="msf-scope">
            <Checkbox label={t('medicalServiceForm.props.assistanceScope')} checked={isAssistanceScope} onChange={(_, data) => setIsAssistanceScope(!!data.checked)} />
            <Checkbox label={t('medicalServiceForm.props.protocolScope')} checked={isProtocolScope} onChange={(_, data) => setIsProtocolScope(!!data.checked)} />
          </div>
        </GuideField>

        <GuideField
          label={t('medicalServiceForm.props.parentService')}
          hint={t('medicalServiceForm.props.parentServiceHint')}
          error={submittedForm && parentServiceId !== null && parentServiceId === id ? t('medicalServiceForm.formValidation.parentIsSelf') : undefined}
        >
          <div className="msf-parent">
            <GuideHelpText data-testid="medical-service-parent-current">
              {parentServiceId !== null
                ? <><strong>{t('medicalServiceForm.props.parentCurrent')}:</strong> {parentServiceName ?? `#${parentServiceId}`}</>
                : t('medicalServiceForm.props.parentNone')}
            </GuideHelpText>
            {parentServiceId !== null && (
              <Button appearance="subtle" size="small" icon={<Dismiss12Regular />} onClick={() => { setParentServiceId(null); setParentServiceName(undefined); }}>
                {t('medicalServiceForm.props.parentRemove')}
              </Button>
            )}
            <MedicalServiceSelectorComponent
              MultiselectEnable={false}
              MedicalServicesSelectedIds={parentServiceId !== null ? [parentServiceId] : undefined}
              onSelectedServices={(services) => {
                const picked = services && services.length > 0 ? services[0] : undefined;
                setParentServiceId(picked ? picked.id : null);
                setParentServiceName(picked?.serviceNameReference);
              }}
            />
          </div>
        </GuideField>
      </GuideDrawerSection>
      {props.MedicalService && (
        <GuideDrawerSection
          title={t('medicalServiceForm.props.specialtiesLabel')}
          actions={
            <Button
              appearance="subtle"
              icon={<Add20Regular />}
              onClick={() => setIsSpecialtyPanelOpen(true)}
              data-testid="medical-service-add-specialty"
            >
              {t('medicalServiceForm.props.specialtiesButton')}
            </Button>
          }
        >
          {props.MedicalService.specialties && props.MedicalService.specialties.length > 0 ? (
            <div className={styles.pills}>
              {props.MedicalService.specialties.map((s: MedicalServiceSpecialty) => (
                <span key={s.id} className={styles.pill}>
                  {s.specialtyName ?? String(s.specialtyId)}
                  <Button
                    appearance="transparent"
                    size="small"
                    className={styles.pillRemove}
                    icon={<Dismiss12Regular />}
                    title="Quitar"
                    aria-label="Quitar"
                    onClick={() => props.RemoveMedicalServiceSpecialty(s.medicalServiceId, s.id)}
                  />
                </span>
              ))}
            </div>
          ) : (
            <GuideHelpText className={styles.empty}>
              {t('medicalServiceForm.props.noSpecialties')}
            </GuideHelpText>
          )}
        </GuideDrawerSection>
      )}
    </>
  );

  // form validation

  const ValidateForm = (): boolean => {
    var validationResult: boolean = true;

    var FormValidation: FormValidation = { success: true, errors: [] };
    setFormValidationStatus(FormValidation);

    if (isLinkedToSnowmed && snowmedCode === undefined) {
      validationResult = false;
      FormValidation.errors.push(t('medicalServiceForm.formValidation.selectService'));
    }

    if (
      serviceNameReference === undefined ||
      serviceNameReference.length === 0
    ) {
      validationResult = false;
      FormValidation.errors.push(t('medicalServiceForm.formValidation.referenceName'));
    }

    if (parentServiceId !== null && id !== undefined && parentServiceId === id) {
      validationResult = false;
      FormValidation.errors.push(t('medicalServiceForm.formValidation.parentIsSelf'));
    }

    FormValidation.success = validationResult;
    setFormValidationStatus(FormValidation);
    return validationResult;
  };

  return (
    <GuideDrawer
      open={props.isOpen}
      size="medium"
      eyebrow={props.MedicalService?.serviceNameReference || undefined}
      title={t('medicalServiceForm.modal.title')}
      onDismiss={props.dismissPanel}
      preventDismiss={isSaving}
      testId="medical-service-drawer"
      secondaryAction={{ label: t('medicalServiceForm.footer.cancelButton'), onClick: HandleCancelForm }}
      primaryAction={{
        label: t('medicalServiceForm.footer.saveButton'),
        onClick: HandleSaveForm,
        loading: isSaving
      }}
    >
      {submittedForm && !FormValidationStatus?.success && (
        <GuideMessageBar intent="error" layout="multiline">
          <MessageBarBody>
            <MessageBarTitle>{t('medicalServiceForm.modal.reviewInformation')}</MessageBarTitle>
            <ul>
              {FormValidationStatus?.errors.map(
                (Error: string, index: number) => (
                  <li key={index}>{Error}</li>
                )
              )}
            </ul>
          </MessageBarBody>
        </GuideMessageBar>
      )}
      {submittedForm &&
        MedicalServicesState &&
        (MedicalServicesState.failOnAddingNewOne ||
          MedicalServicesState.failOnUpdatingOne) &&
        MedicalServicesState.error && (
          <ErrorMessageComponent Error={MedicalServicesState.error} />
        )}
      {/* Sin <form>: Enter en el buscador de prácticas elige una sugerencia, no guarda. */}
      {formFields}
      <MedicalServiceCategoriesDrawerComponent
        isOpen={categoriesDrawerOpen}
        onClose={() => setCategoriesDrawerOpen(false)}
        onChanged={categories.reload}
      />
      <SpecialtyMultiSelector
        isOpen={isSpecialtyPanelOpen}
        openPanel={() => setIsSpecialtyPanelOpen(true)}
        dismissPanel={() => setIsSpecialtyPanelOpen(false)}
        SelectedSpecialties={selectedSpecialties}
        ErrorInformed={MedicalServicesState?.errorSpecialties}
        onSelect={handleSpecialtySelect}
        onUnselect={handleSpecialtyUnselect}
      />
    </GuideDrawer>
  );
};


export default connect(
  ownFunctions.mapStateToProps,
  ownFunctions.mapDispatchToProps
)(MedicalServicesFormComponent as any);
